// Shared, dependency-free policy and wire formats. Provider IO is injected in emailSyncEngine.ts.
import { senderMatchesAllowlist } from './emailAllowlist.js';
import { htmlToPlainText, looksLikeHtmlBody } from './emailBodyText.js';
export function syncBody(bodyContent = '', bodyContentType = 'text') {
    const body = String(bodyContent);
    return bodyContentType.toLowerCase() === 'html' || looksLikeHtmlBody(body) ? htmlToPlainText(body) : body.replace(/\r\n?/g,'\n').trim();
}
export function syncFolders(provider) {
    const chosen = provider?.folder_sync?.folders;
    return [...new Set(['Inbox', 'Sent', ...(Array.isArray(chosen) ? chosen : [])]
        .filter((v) => typeof v === 'string' && v.trim() && !/[\r\n\0]/.test(v)).map((v) => v.trim()))];
}
export function syncHistoryDays(provider) {
    const days = Number(provider?.folder_sync?.history_days ?? 7);
    return Number.isInteger(days) && days >= 1 && days <= 30 ? days : 7;
}
// Arrival backstop is separate from the initial-history import window.
export function syncLiveArrivalHours(provider) {
    const hours = Number(provider?.folder_sync?.live_arrival_hours ?? 24);
    return Number.isInteger(hours) && hours >= 1 && hours <= 168 ? hours : 24;
}
export function syncArrivalWithinWindow(provider, entry, now = Date.now()) {
    const arrival = Date.parse(entry?.at);
    return Number.isFinite(arrival) && arrival >= now - syncLiveArrivalHours(provider) * 3600000 && arrival <= now + 300000;
}
export function providerFolder(provider, folder) {
    if (provider === 'gmail') return ({ Inbox: 'INBOX', Sent: 'SENT', Trash: 'TRASH', Archive: 'ALL' })[folder] || folder;
    if (provider === 'microsoft_graph') return ({ Inbox: 'inbox', Sent: 'sentitems', Trash: 'deleteditems', Archive: 'archive' })[folder] || folder;
    return folder === 'Inbox' ? 'INBOX' : folder;
}
const address = (v) => String(v || '').trim().toLowerCase();
export function syncMessageAllowed(provider, message, listedAiTestSender = false) {
    // Sent is checked against EVERY external To/Cc recipient, never against the mailbox's own address.
    // A mixed allowed/excluded recipient list is refused rather than exposing the excluded correspondence.
    const peers = message.direction === 'outbound' ? [...(message.to || []), ...(message.cc || []), ...(message.bcc || [])].filter((v) => address(v) !== address(provider.from_email)) : [message.from];
    return peers.length > 0 && peers.every((peer) => !senderMatchesAllowlist(provider.sender_blocklist, peer)
        && (provider.access_mode === 'full' || (message.direction === 'inbound' && listedAiTestSender) || senderMatchesAllowlist(provider.sender_allowlist, peer)));
}
export function graphSyncEntry(message, folder, mailbox) {
    const emails = (list) => (list || []).map((v) => v.emailAddress?.address).filter(Boolean);
    const headers = Object.fromEntries((message.internetMessageHeaders || []).map((v) => [v.name.toLowerCase(), v.value]));
    return {
        provider_message_id: message.id, graph_message_id: message.id, internet_message_id: message.internetMessageId || null,
        conversation_id: message.conversationId || null, thread_id: message.conversationId || null,
        in_reply_to: headers['in-reply-to'] || null, references: headers.references || null,
        channel: 'email', direction: address(message.from?.emailAddress?.address) === address(mailbox) ? 'outbound' : 'inbound',
        from: message.from?.emailAddress?.address || '', to: emails(message.toRecipients), cc: emails(message.ccRecipients), bcc: emails(message.bccRecipients),
        subject: String(message.subject || '').replace(/[\r\n]/g, ' '),
        body: syncBody(message.body?.content || '', message.body?.contentType || 'text'),
        is_read: message.isRead === true, email_folder: folder, deleted: false,
        at: address(message.from?.emailAddress?.address) === address(mailbox) ? (message.sentDateTime || message.receivedDateTime) : (message.receivedDateTime || message.sentDateTime),
    };
}
export function gmailSyncEntry(message, folders, mailbox) {
    const headers = Object.fromEntries((message.payload?.headers || []).map((v) => [v.name.toLowerCase(), v.value]));
    const emails = (value) => (String(value || '').match(/[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || []);
    const decode = (data) => { try { return decodeURIComponent(escape(atob(String(data || '').replace(/-/g, '+').replace(/_/g, '/')))); } catch { return ''; } };
    const parts = (p) => [p, ...(p.parts || []).flatMap(parts)];
    const all = parts(message.payload || {});
    const plain = all.find((p) => p.mimeType === 'text/plain' && p.body?.data);
    const html = all.find((p) => p.mimeType === 'text/html' && p.body?.data);
    const from = emails(headers.from)[0] || '';
    const labels = message.labelIds || [];
    return {
        provider_message_id: message.id, gmail_message_id: message.id, internet_message_id: headers['message-id'] || null,
        thread_id: message.threadId, in_reply_to: headers['in-reply-to'] || null, references: headers.references || null,
        channel: 'email', direction: labels.includes('SENT') || address(from) === address(mailbox) ? 'outbound' : 'inbound',
        from, to: emails(headers.to), cc: emails(headers.cc), bcc: emails(headers.bcc), subject: String(headers.subject || '').replace(/[\r\n]/g, ' '),
        body: syncBody(decode((plain || html)?.body?.data), plain ? 'text' : 'html'),
        is_read: !labels.includes('UNREAD'), email_folder: folders.find((f) => labels.includes(providerFolder('gmail', f))) || 'Archive',
        deleted: labels.includes('TRASH'), outside_folders: !folders.some((f) => labels.includes(providerFolder('gmail', f))),
        at: new Date(Number(message.internalDate)).toISOString(),
    };
}
export function emailMutation(provider, message, action, value) {
    const id = encodeURIComponent(message.provider_message_id);
    if (!['read', 'delete', 'move'].includes(action)) throw new Error('Unsupported mailbox action');
    if (provider.provider_type === 'microsoft_graph') {
        const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(provider.from_email)}/messages/${id}`;
        return action === 'read' ? { url, method: 'PATCH', body: { isRead: value === true } }
            : { url: `${url}/move`, method: 'POST', body: { destinationId: action === 'delete' ? 'deleteditems' : providerFolder('microsoft_graph', value) } };
    }
    if (provider.provider_type === 'gmail') {
        const url = `https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}`;
        if (action === 'delete') return { url: `${url}/trash`, method: 'POST', body: {} };
        if (action === 'move' && ['SENT','DRAFT','UNREAD','STARRED','IMPORTANT','SPAM','TRASH'].includes(providerFolder('gmail', value))) throw new Error('Choose Inbox, Archive or a custom Gmail label');
        return { url: `${url}/modify`, method: 'POST', body: action === 'read'
            ? { [value ? 'removeLabelIds' : 'addLabelIds']: ['UNREAD'] }
            : { addLabelIds: providerFolder('gmail', value) === 'ALL' ? [] : [providerFolder('gmail', value)], removeLabelIds: [providerFolder('gmail', message.email_folder)].filter((f) => !['SENT','ALL','DRAFT'].includes(f) && f !== providerFolder('gmail', value)) } };
    }
    return { uid: message.imap_uid, uidValidity: message.imap_uidvalidity, folder: message.email_folder, action, value: action === 'delete' ? 'Trash' : value };
}
